import { useEffect, useMemo, useState } from 'react';
import { ACTIVE_COMPANY_KEY, api } from '../api.js';
import { PALETTE } from '../palette.js';
import { Button, Card, Pill } from '../components/primitives.jsx';
import ProtectedPageLoader from '../components/ProtectedPageLoader.jsx';
import { IconCheck, IconChevron, IconInfo, IconLightning, IconRefresh, IconSettings, IconTarget } from '../components/icons.jsx';
import { safeFailureMessage } from '../safe-failure.js';

function tenantHeaders(companyId) {
  return companyId ? { headers: { 'x-company-id': companyId } } : undefined;
}

function getActiveCompanyId(session) {
  const memberships = session.user?.memberships || [];
  return session.user?.activeCompany?.id || memberships[0]?.companyId || '';
}

function statusLabel(status) {
  if (status === 'ready') return 'Ready';
  if (status === 'blocked') return 'Blocked';
  if (status === 'attention') return 'Attention';
  return 'Unknown';
}

function statusTone(status) {
  if (status === 'ready') return 'live';
  if (status === 'blocked') return 'warn';
  if (status === 'attention') return 'accent';
  return 'neutral';
}

function statusColor(status) {
  if (status === 'ready') return PALETTE.success;
  if (status === 'blocked') return PALETTE.warn;
  if (status === 'attention') return PALETTE.accent;
  return PALETTE.ink3;
}

function formatCheckedAt(value) {
  if (!value) return '-';
  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function OnboardingChecklist({ session, navigate }) {
  const activeCompanyId = getActiveCompanyId(session);
  const isSaasSession = session.user?.auth?.type === 'supabase';
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [checklist, setChecklist] = useState(null);

  async function loadChecklist() {
    if (!isSaasSession || !activeCompanyId) return;
    setStatus('loading');
    setError(null);
    try {
      const response = await api.get('/api/saas/onboarding/readiness', tenantHeaders(activeCompanyId));
      setChecklist(response.checklist || null);
      setStatus('ready');
      try {
        localStorage.setItem(ACTIVE_COMPANY_KEY, activeCompanyId);
      } catch {}
    } catch (err) {
      setError(err);
      setStatus('error');
    }
  }

  useEffect(() => {
    loadChecklist();
  }, [isSaasSession, activeCompanyId]);

  const summary = useMemo(() => ({
    blockers: checklist?.counts?.blockers || 0,
    optional: checklist?.counts?.optional || 0,
    brands: checklist?.counts?.brands || 0,
    campaigns: checklist?.counts?.campaigns || 0,
  }), [checklist]);
  const showInitialChecklistLoading =
    isSaasSession &&
    Boolean(activeCompanyId) &&
    !checklist &&
    (status === 'idle' || status === 'loading');

  if (!isSaasSession) {
    return (
      <PageShell>
        <Header />
        <EmptyState title="SaaS session unavailable" body="Sign in with a SaaS account to view launch readiness." />
      </PageShell>
    );
  }

  if (showInitialChecklistLoading) {
    return (
      <PageShell>
        <Header
          companyName={session.user?.activeCompany?.name}
          onRefresh={loadChecklist}
          loading
        />
        <ProtectedPageLoader />
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Header
        companyName={checklist?.company?.name || session.user?.activeCompany?.name}
        ready={checklist?.ready}
        checkedAt={checklist?.checkedAt}
        onRefresh={loadChecklist}
        loading={status === 'loading'}
      />

      {status === 'error' && (
        <div style={{ marginBottom: 16 }}>
          <EmptyState tone="warn" title="Readiness unavailable" body={safeFailureMessage(error, 'onboarding')} />
        </div>
      )}

      <section style={summaryGridStyle}>
        <Metric label="Blocking items" value={String(summary.blockers)} tone={summary.blockers ? 'warn' : 'live'} />
        <Metric label="Optional items" value={String(summary.optional)} tone={summary.optional ? 'accent' : 'neutral'} />
        <Metric label="Brands" value={String(summary.brands)} tone="neutral" />
        <Metric label="Campaigns" value={String(summary.campaigns)} tone="neutral" />
      </section>

      {checklist ? (
        <>
          {checklist.blockers?.length > 0 && (
            <section style={blockerPanelStyle}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <IconInfo size={16} />
                <div style={{ fontWeight: 600, color: PALETTE.ink }}>Blocking items</div>
              </div>
              <div style={{ display: 'grid', gap: 8 }}>
                {checklist.blockers.slice(0, 6).map((blocker) => (
                  <div key={`${blocker.sectionId}-${blocker.itemId}`} style={blockerRowStyle}>
                    <span style={{ color: PALETTE.ink, fontWeight: 500 }}>{blocker.label}</span>
                    <span style={{ color: PALETTE.ink2 }}>{blocker.message}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section style={sectionGridStyle}>
            {(checklist.sections || []).map((section) => (
              <ChecklistSection key={section.id} section={section} navigate={navigate} />
            ))}
          </section>
        </>
      ) : (
        <EmptyState title="No checklist returned" body="The readiness endpoint returned no checklist for this company." />
      )}
    </PageShell>
  );
}

function PageShell({ children }) {
  return (
    <div style={{
      padding: 'clamp(18px, 4vw, 38px) clamp(16px, 4vw, 38px) 64px',
      maxWidth: 1240,
      margin: '0 auto',
      fontFamily: 'Inter, sans-serif',
      color: PALETTE.ink,
    }}>
      {children}
    </div>
  );
}

function Header({ companyName, ready, checkedAt, onRefresh, loading }) {
  return (
    <header style={{
      display: 'flex',
      alignItems: 'end',
      justifyContent: 'space-between',
      gap: 18,
      marginBottom: 24,
      flexWrap: 'wrap',
    }}>
      <div>
        <div style={{ fontSize: 11, letterSpacing: 0, textTransform: 'uppercase', color: PALETTE.ink3, marginBottom: 6 }}>
          Onboarding
        </div>
        <h1 style={{
          fontFamily: 'Instrument Serif, serif',
          fontStyle: 'italic',
          fontSize: 'clamp(34px, 5vw, 48px)',
          fontWeight: 400,
          margin: 0,
          color: PALETTE.ink,
          letterSpacing: 0,
        }}>
          Launch readiness
        </h1>
        <div style={{ marginTop: 8, color: PALETTE.ink2, fontSize: 13.5 }}>
          {companyName || 'Active company'} · {checkedAt ? `Checked ${formatCheckedAt(checkedAt)}` : 'Not checked yet'}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {typeof ready === 'boolean' && (
          <Pill tone={ready ? 'live' : 'warn'}>
            {ready ? <IconCheck size={12} /> : <IconInfo size={12} />}
            {ready ? 'Ready to build' : 'Blocked'}
          </Pill>
        )}
        <Button variant="ghost" size="sm" onClick={onRefresh} disabled={loading}>
          <IconRefresh size={14} />
          Refresh
        </Button>
      </div>
    </header>
  );
}

function Metric({ label, value, tone }) {
  const color = tone === 'warn'
    ? PALETTE.warn
    : tone === 'live'
      ? PALETTE.success
      : tone === 'accent'
        ? PALETTE.accent
        : PALETTE.ink;
  return (
    <div style={metricStyle}>
      <div style={{ fontSize: 11, color: PALETTE.ink3, textTransform: 'uppercase', letterSpacing: 0 }}>{label}</div>
      <div style={{ color, fontSize: 28, fontWeight: 600, lineHeight: 1.1 }}>{value}</div>
    </div>
  );
}

function ChecklistSection({ section, navigate }) {
  return (
    <Card style={{
      padding: 0,
      overflow: 'hidden',
      borderColor: section.status === 'blocked' ? '#EBDCB2' : PALETTE.line,
    }}>
      <div style={{
        padding: 18,
        borderBottom: `1px solid ${PALETTE.lineSoft}`,
        display: 'flex',
        justifyContent: 'space-between',
        gap: 14,
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 6 }}>
            <span style={{
              width: 9,
              height: 9,
              borderRadius: '50%',
              background: statusColor(section.status),
              flex: '0 0 auto',
            }} />
            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: PALETTE.ink, letterSpacing: 0 }}>
              {section.title}
            </h2>
          </div>
          <p style={{ margin: 0, color: PALETTE.ink2, fontSize: 13, lineHeight: 1.45 }}>
            {section.summary}
          </p>
        </div>
        <Pill tone={statusTone(section.status)} style={{ alignSelf: 'flex-start' }}>
          {statusLabel(section.status)}
        </Pill>
      </div>

      <div style={{ display: 'grid' }}>
        {(section.items || []).map((entry) => (
          <ChecklistItem key={entry.id} item={entry} />
        ))}
      </div>

      {section.action && section.actionRoute && (
        <div style={{ padding: '14px 18px 18px' }}>
          <Button
            variant={section.status === 'blocked' ? 'accent' : 'ghost'}
            size="sm"
            onClick={() => navigate(section.actionRoute)}
          >
            {section.status === 'blocked' ? <IconTarget size={14} /> : <IconLightning size={14} />}
            {section.action}
            <IconChevron size={13} />
          </Button>
        </div>
      )}
    </Card>
  );
}

function ChecklistItem({ item }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'minmax(140px, 0.6fr) minmax(0, 1fr)',
      gap: 14,
      padding: '13px 18px',
      borderBottom: `1px solid ${PALETTE.lineSoft}`,
      alignItems: 'start',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
        <span style={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          display: 'inline-grid',
          placeItems: 'center',
          border: `1px solid ${statusColor(item.status)}`,
          color: statusColor(item.status),
          flex: '0 0 auto',
        }}>
          {item.status === 'ready' ? <IconCheck size={11} /> : <IconInfo size={11} />}
        </span>
        <span style={{ color: PALETTE.ink, fontSize: 13.5, fontWeight: 500, overflowWrap: 'anywhere' }}>
          {item.label}
        </span>
      </div>
      <div>
        <div style={{ color: PALETTE.ink2, fontSize: 13, lineHeight: 1.45, overflowWrap: 'anywhere' }}>
          {item.message}
        </div>
        {item.required === false && (
          <div style={{ marginTop: 5, fontSize: 11.5, color: PALETTE.ink3 }}>Optional</div>
        )}
      </div>
    </div>
  );
}

function EmptyState({ title, body, tone = 'neutral' }) {
  const warn = tone === 'warn';
  return (
    <div style={{
      padding: 22,
      background: warn ? PALETTE.warnSoft : PALETTE.surface,
      border: `1px solid ${warn ? '#EBDCB2' : PALETTE.line}`,
      borderRadius: 14,
    }}>
      <div style={{ fontWeight: 600, marginBottom: 5, color: PALETTE.ink }}>{title}</div>
      <div style={{ color: PALETTE.ink2, fontSize: 13, lineHeight: 1.45 }}>{body}</div>
    </div>
  );
}

const summaryGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
  gap: 12,
  marginBottom: 18,
};

const metricStyle = {
  background: PALETTE.surface,
  border: `1px solid ${PALETTE.line}`,
  borderRadius: 14,
  padding: '16px 18px',
  display: 'grid',
  gap: 8,
};

const blockerPanelStyle = {
  background: PALETTE.warnSoft,
  border: '1px solid #EBDCB2',
  borderRadius: 14,
  padding: 18,
  marginBottom: 18,
};

const blockerRowStyle = {
  display: 'grid',
  gridTemplateColumns: 'minmax(140px, 0.5fr) minmax(0, 1fr)',
  gap: 12,
  fontSize: 13,
  lineHeight: 1.45,
};

const sectionGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))',
  gap: 16,
};
